import { useCallback, useMemo } from 'react';
import { extractPurchaseRequired, FEATURE_META } from '../api/billingApi';
import { useNexusBilling } from '../context/NexusBillingContext';
import { describeUsage } from '../components/NexusUsageBadge';
import { isNexusBuilderUi, nexusRequestHeaders, readNexusUsageHeader } from '../api/nexusLearnerApi';

/**
 * One hook the reused learner components call to become billing-aware in TSG
 * Nexus — and to stay completely inert everywhere else.
 *
 *   const nexus = useNexusLearnerBilling('AI_TUTOR', { subFeature: 'CODE_REVIEW' });
 *   fetch(url, { headers: { ...base, ...nexus.headers() } });
 *   if (res.status === 402 && nexus.handleQuota(body)) return;   // modal opened
 *   nexus.noteUsage(res);                                        // refresh counters
 *
 * `nexus.isNexus` is false in the learner app and in college portals, so every
 * call site can guard its UI with a single flag. No quota or price is ever
 * computed here: the server owns every number.
 */
const useNexusLearnerBilling = (feature, { subFeature, technology } = {}) => {
  const { features, summary, openQuotaModal, refreshSummary, refreshAll } = useNexusBilling();
  const isNexus = isNexusBuilderUi();

  const usage = useMemo(
    () => (isNexus ? describeUsage(features, { feature, subFeature, technology }) : null),
    [isNexus, features, feature, subFeature, technology],
  );

  /** Subtle "you are nearly out" line — only when free is nearly gone AND nothing paid is left. */
  const hint = useMemo(() => {
    if (!usage || usage.legacy) return null;
    const { freeRemaining, paidRemaining } = usage;
    if (paidRemaining > 0) return null;
    if (freeRemaining == null || freeRemaining > 2) return null;
    const meta = FEATURE_META[feature] || { unit: 'unit', units: 'units' };
    const noun = freeRemaining === 1 ? meta.unit : meta.units;
    if (freeRemaining <= 0) return `No free ${meta.units} left this month`;
    return `${freeRemaining} free ${noun} remaining this month`;
  }, [usage, feature]);

  /** Nexus identity + idempotency headers ({} outside Nexus). */
  const headers = useCallback(() => nexusRequestHeaders(), []);

  /**
   * Handle a structured 402 from a metered learner endpoint.
   * Accepts an axios error, a fetch-parsed body, or a BillingApiError.
   * Returns the payload it opened the modal with, or null when it isn't one.
   */
  const handleQuota = useCallback((errOrBody) => {
    if (!isNexus) return null;
    const payload = extractPurchaseRequired(errOrBody);
    if (!payload) return null;
    openQuotaModal(payload);
    return payload;
  }, [isNexus, openQuotaModal]);

  /** After a successful metered call: re-read the server's counters. */
  const noteUsage = useCallback((res) => {
    if (!isNexus) return null;
    const header = readNexusUsageHeader(res);
    refreshSummary();
    return header;
  }, [isNexus, refreshSummary]);

  return { isNexus, usage, hint, headers, handleQuota, noteUsage, openQuotaModal, refreshSummary, refreshAll, features, summary };
};

export default useNexusLearnerBilling;
export { useNexusLearnerBilling };
